
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * SwapImportAccountGroupSettings struct
 */
class SwapImportAccountGroupSettings {
  /**
   * Initialize struct
   * @param {!string=} accountGroupMask
   * @param {!boolean=} isSwapEnabledAccountGroup
   * @constructor
   */
  constructor (accountGroupMask = '', isSwapEnabledAccountGroup = false) {
    this.AccountGroupMask = accountGroupMask
    this.IsSwapEnabledAccountGroup = isSwapEnabledAccountGroup
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SwapImportAccountGroupSettings}
   * @param {!SwapImportAccountGroupSettings} other Other struct
   * @returns {!SwapImportAccountGroupSettings} This struct
   */
  copy (other) {
    if (other.AccountGroupMask != null) {
      this.AccountGroupMask = other.AccountGroupMask
    } else {
      this.AccountGroupMask = null
    }
    if (other.IsSwapEnabledAccountGroup != null) {
      this.IsSwapEnabledAccountGroup = other.IsSwapEnabledAccountGroup
    } else {
      this.IsSwapEnabledAccountGroup = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SwapImportAccountGroupSettings}
   * @returns {!SwapImportAccountGroupSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SwapImportAccountGroupSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SwapImportAccountGroupSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SwapImportAccountGroupSettings}
   * @param {!SwapImportAccountGroupSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SwapImportAccountGroupSettings)) {
      throw new TypeError('Instance of SwapImportAccountGroupSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SwapImportAccountGroupSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      AccountGroupMask: ((this.AccountGroupMask != null) ? this.AccountGroupMask : null),
      IsSwapEnabledAccountGroup: ((this.IsSwapEnabledAccountGroup != null) ? this.IsSwapEnabledAccountGroup : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SwapImportAccountGroupSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SwapImportAccountGroupSettings} other Object value
   * @returns {!SwapImportAccountGroupSettings} Created struct
   */
  static fromObject (other) {
    return new SwapImportAccountGroupSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SwapImportAccountGroupSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SwapImportAccountGroupSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SwapImportAccountGroupSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 185
  }
}

export { SwapImportAccountGroupSettings };

/**
 * Fast Binary Encoding SwapImportAccountGroupSettings field model
 */
class FieldModelSwapImportAccountGroupSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.AccountGroupMask = new fbe.FieldModelString(buffer, 4 + 4)
    this.IsSwapEnabledAccountGroup = new fbe.FieldModelBool(buffer, this.AccountGroupMask.fbeOffset + this.AccountGroupMask.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.AccountGroupMask.fbeSize + this.IsSwapEnabledAccountGroup.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.AccountGroupMask.fbeExtra + this.IsSwapEnabledAccountGroup.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSwapImportAccountGroupSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 185
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountGroupMask.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.AccountGroupMask.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountGroupMask.fbeSize

    if ((fbeCurrentSize + this.IsSwapEnabledAccountGroup.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsSwapEnabledAccountGroup.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsSwapEnabledAccountGroup.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {!SwapImportAccountGroupSettings} fbeValue Default value, defaults is new SwapImportAccountGroupSettings()
   * @returns {!SwapImportAccountGroupSettings} SwapImportAccountGroupSettings value
   */
  get (fbeValue = new SwapImportAccountGroupSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {!SwapImportAccountGroupSettings} fbeValue SwapImportAccountGroupSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.AccountGroupMask.fbeSize) <= fbeStructSize) {
      fbeValue.AccountGroupMask = this.AccountGroupMask.get()
    } else {
      fbeValue.AccountGroupMask = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.AccountGroupMask.fbeSize

    if ((fbeCurrentSize + this.IsSwapEnabledAccountGroup.fbeSize) <= fbeStructSize) {
      fbeValue.IsSwapEnabledAccountGroup = this.IsSwapEnabledAccountGroup.get()
    } else {
      fbeValue.IsSwapEnabledAccountGroup = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsSwapEnabledAccountGroup.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {!SwapImportAccountGroupSettings} fbeValue SwapImportAccountGroupSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSwapImportAccountGroupSettings}
   * @param {SwapImportAccountGroupSettings} fbeValue SwapImportAccountGroupSettings value
   */
  setFields (fbeValue) {
    this.AccountGroupMask.set(fbeValue.AccountGroupMask)
    this.IsSwapEnabledAccountGroup.set(fbeValue.IsSwapEnabledAccountGroup)
  }
}

export { FieldModelSwapImportAccountGroupSettings };

/**
 * Fast Binary Encoding SwapImportAccountGroupSettings model
 */
class SwapImportAccountGroupSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSwapImportAccountGroupSettings(this.buffer, 4)
  }

  /**
   * Get the SwapImportAccountGroupSettings model
   * @this {!SwapImportAccountGroupSettingsModel}
   * @returns {!FieldModelSwapImportAccountGroupSettings} model SwapImportAccountGroupSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SwapImportAccountGroupSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SwapImportAccountGroupSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SwapImportAccountGroupSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SwapImportAccountGroupSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSwapImportAccountGroupSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SwapImportAccountGroupSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SwapImportAccountGroupSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SwapImportAccountGroupSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SwapImportAccountGroupSettingsModel}
   * @param {!SwapImportAccountGroupSettings} value SwapImportAccountGroupSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SwapImportAccountGroupSettingsModel}
   * @param {!SwapImportAccountGroupSettings} value SwapImportAccountGroupSettings value, defaults is new SwapImportAccountGroupSettings()
   * @return {!object} Deserialized SwapImportAccountGroupSettings value and its size
   */
  deserialize (value = new SwapImportAccountGroupSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SwapImportAccountGroupSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SwapImportAccountGroupSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SwapImportAccountGroupSettingsModel}
   * @param {!number} prev Previous SwapImportAccountGroupSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SwapImportAccountGroupSettingsModel };
